import {
  normalizeSearch,
  planExerciseSlugs,
  planMuscles,
  programExerciseSlugs,
  programMuscles,
  type EquipmentKind,
  type Exercise,
  type Locale,
} from "@jackedlogs/shared";

import { libraryItemName, type LibraryItem } from "./useLibrary";

// Pure filter logic for the Library browser (HR-S3c) — same shape as the
// Catalog's computeCatalogSections: normalized-substring search, then
// OR-within-axis / AND-across-axes on the tag sets. An item that lacks an
// axis entirely (a plan has no per-week frequency, a personal row has no
// difficulty) simply doesn't match while that axis is active — the axis
// filters FOR the attribute, it doesn't grandfather unknowns in.

export type LibraryDifficulty = "beginner" | "intermediate" | "advanced";

export type LibraryTagFilters = {
  query: string;
  muscles: ReadonlySet<string>;
  equipment: ReadonlySet<EquipmentKind>;
  levels: ReadonlySet<LibraryDifficulty>;
  perWeek: ReadonlySet<number>;
};

export const emptyLibraryFilters: LibraryTagFilters = {
  query: "",
  muscles: new Set(),
  equipment: new Set(),
  levels: new Set(),
  perWeek: new Set(),
};

/** How many axes are active — the count on the "Filters" toggle. */
export const activeAxisCount = (f: LibraryTagFilters): number =>
  (f.muscles.size > 0 ? 1 : 0) +
  (f.equipment.size > 0 ? 1 : 0) +
  (f.levels.size > 0 ? 1 : 0) +
  (f.perWeek.size > 0 ? 1 : 0);

type LibraryItemTags = {
  muscles: string[];
  equipment: EquipmentKind[];
  difficulty?: LibraryDifficulty;
  perWeek?: number;
};

const equipmentOf = (
  slugs: ReadonlyArray<string>,
  catalogBySlug: ReadonlyMap<string, Exercise>,
): EquipmentKind[] => {
  const set = new Set<EquipmentKind>();
  for (const slug of slugs) {
    const kind = catalogBySlug.get(slug)?.equipment;
    if (kind) set.add(kind);
  }
  return Array.from(set);
};

export const libraryItemTags = (
  item: LibraryItem,
  catalogBySlug: ReadonlyMap<string, Exercise>,
): LibraryItemTags => {
  if (item.kind === "program") {
    const sessions =
      item.source === "personal" ? item.program.sessions : item.template.sessions;
    return {
      muscles: programMuscles(sessions, catalogBySlug),
      equipment: equipmentOf(programExerciseSlugs(sessions), catalogBySlug),
      ...(item.source === "official"
        ? { difficulty: item.template.difficulty }
        : {}),
      perWeek:
        item.source === "personal"
          ? item.program.sessionsPerWeek
          : item.template.sessionsPerWeek,
    };
  }
  const entries =
    item.source === "personal" ? item.plan.entries : item.template.entries;
  return {
    muscles: planMuscles(entries, catalogBySlug),
    equipment: equipmentOf(planExerciseSlugs(entries), catalogBySlug),
    ...(item.source === "official"
      ? { difficulty: item.template.difficulty }
      : {}),
  };
};

const intersects = <T>(
  active: ReadonlySet<T>,
  values: ReadonlyArray<T>,
): boolean => values.some((v) => active.has(v));

export const applyLibraryFilters = (
  items: ReadonlyArray<LibraryItem>,
  filters: LibraryTagFilters,
  catalogBySlug: ReadonlyMap<string, Exercise>,
  // PE-21 — the reader's language: search matches what they can SEE on
  // the card, not a language the resolution happened to pick.
  locale?: Locale,
): LibraryItem[] => {
  const q = normalizeSearch(filters.query.trim());
  return items.filter((item) => {
    if (q.length > 0) {
      const inName = normalizeSearch(libraryItemName(item, locale)).includes(q);
      const inDesc =
        item.source === "official" &&
        normalizeSearch(item.template.description).includes(q);
      if (!inName && !inDesc) return false;
    }
    if (
      filters.muscles.size === 0 &&
      filters.equipment.size === 0 &&
      filters.levels.size === 0 &&
      filters.perWeek.size === 0
    )
      return true;
    const tags = libraryItemTags(item, catalogBySlug);
    if (filters.muscles.size > 0 && !intersects(filters.muscles, tags.muscles))
      return false;
    if (
      filters.equipment.size > 0 &&
      !intersects(filters.equipment, tags.equipment)
    )
      return false;
    if (
      filters.levels.size > 0 &&
      (tags.difficulty === undefined || !filters.levels.has(tags.difficulty))
    )
      return false;
    if (
      filters.perWeek.size > 0 &&
      (tags.perWeek === undefined || !filters.perWeek.has(tags.perWeek))
    )
      return false;
    return true;
  });
};

export type LibraryFilterOptions = {
  muscles: string[];
  equipment: EquipmentKind[];
  levels: LibraryDifficulty[];
  perWeek: number[];
};

/** Options actually present across the whole item set — an axis chip you
 *  can select always matches at least one item. Unsorted except perWeek
 *  (numeric); muscle sort-by-localized-name is the caller's concern. */
export const collectLibraryFilterOptions = (
  items: ReadonlyArray<LibraryItem>,
  catalogBySlug: ReadonlyMap<string, Exercise>,
): LibraryFilterOptions => {
  const muscles = new Set<string>();
  const equipment = new Set<EquipmentKind>();
  const levels = new Set<LibraryDifficulty>();
  const perWeek = new Set<number>();
  for (const item of items) {
    const tags = libraryItemTags(item, catalogBySlug);
    for (const m of tags.muscles) muscles.add(m);
    for (const e of tags.equipment) equipment.add(e);
    if (tags.difficulty) levels.add(tags.difficulty);
    if (tags.perWeek !== undefined) perWeek.add(tags.perWeek);
  }
  const levelOrder: LibraryDifficulty[] = [
    "beginner",
    "intermediate",
    "advanced",
  ];
  return {
    muscles: Array.from(muscles),
    equipment: Array.from(equipment),
    levels: levelOrder.filter((l) => levels.has(l)),
    perWeek: Array.from(perWeek).sort((a, b) => a - b),
  };
};
